// @import_dependencies
import { store } from 'app_storage/redux-storage';
// @end

// @import services
import storeService from '../storeService';
// @end

// @import utilities
import { ScreenNotificationsUtil } from 'app_utils/screenNotification';
import { Http, HttpCustomStructure } from 'app_utils/http'
// @end

// @import types
import * as InteractionActions from 'app_events/interaction/actions';
import { IMyCourse } from 'app_types/campus/academic/myCoursesTypes';
import * as InteractionTypes from 'app_events/interaction/types';
import { IUserData, TypeAcademicResourceConfigCategories } from 'app_types/userTypes';
import CertificateService from 'app_services/user/certificateService';
import { IPendingCertificate } from 'app_types/user/certificateTypes';
// @end

export interface IAddInteractionParams{
  typeInteraction: InteractionTypes.TypesInteraction;
  survey?: string
  academic_resource_config?: string
  surveyRelated?: string
  config_category?: string;
  shouldReplaceContent?: boolean
  surveyRelatedContent?: {
    name: string
    endDate: string
    teacher: Partial<IUserData>
  }
  pendingCertificate?: IPendingCertificate
}

export interface ICloseInteractionParams{

}

export interface ICheckSurveyAvailableAPIParams{
  moodle_id?: string
}

export interface ICheckSurveyAvailableParams{
  course: IMyCourse
}

class InteractionService {

  /*===============================================
  =            Estructura de un metodo            =
  ================================================
    // La estructura de un metodo debe ser la siguiente:
    public methodName = () => {}
  /*======  End of Estructura de un metodo  =====*/

  private token: string
  private storeObject
  private screenNotification = new ScreenNotificationsUtil()
  private Http: any

  constructor () {
    this.Http = Http
    this.storeObject = store
    this.screenNotification = new ScreenNotificationsUtil()
  }

  refreshAuthToken = () => {
    let auth = this.storeObject.getState().auth
    this.token = (auth.user?.token) ? auth.user.token : ''
  }

  /**
   * @INFO Añadir una nueva interacción
   * @param _params
   */
  public addInteraction = async (_params: IAddInteractionParams, categoryName?: TypeAcademicResourceConfigCategories) => {
    if (!!categoryName) {
      _params.config_category = storeService?.getAcademicResourceConfigCategoryFromName(categoryName)
    }
    this.storeObject.dispatch(InteractionActions.addInteractionAction({
      academic_resource_config: _params.academic_resource_config || '',
      config_category: _params.config_category || '',
      survey: _params.survey || '',
      surveyRelated: _params.surveyRelated || '',
      surveyRelatedContent: _params.surveyRelatedContent || {endDate: '', name: '', teacher: {}},
      typeInteraction: _params.typeInteraction,
      shouldReplaceContent: _params.shouldReplaceContent,
      pendingCertificate: _params?.pendingCertificate,
    }))
  }

  /**
   * @INFO Cerrar una interacción
   * @param _params
   */
  public closeInteraction = async (_params: ICloseInteractionParams) => {

  }

  /**
   * @INFO Verificar la interacción de actualizar información de perfil de usuario
   */
  public checkProfileInformation = () => {
    const user = this.storeObject.getState()?.auth?.user;
    // @INFO Verificar si se debe mostrar la interacción
    if (user?.data?.reviewData?.status && user?.data?.reviewData?.status === 'pending') {
      this.addInteraction({
        typeInteraction: 'update_profile_information'
      })
    }
  }

  public checkHelpStepsInteraction = () => {
    const user = this.storeObject.getState()?.auth?.user;
    if (user?.data?.show_profile_interaction && user?.data?.show_profile_interaction === true) {
      this.addInteraction({
        typeInteraction: 'help_steps'
      })
    }
  }

  /**
   * @INFO Verificar si hay una encuesta y cargarla en el store
   * @param _params
   */
  public checkSurveyAvailable = async (_params?: ICheckSurveyAvailableParams) => {
    const response = await this._checkSurveyAvailable({
      moodle_id: _params?.course._id
    })
    if(response?.code === 200 && response?.survey && response?.academic_resource_config){
      // @INFO Obtener el config category de la encuesta
      const configCategory = storeService?.getAcademicResourceConfigCategoryFromName('survey')
      this.addInteraction({
        academic_resource_config: response.academic_resource_config,
        survey: response.survey,
        surveyRelated: response.surveyRelated,
        surveyRelatedContent: response.surveyRelatedContent,
        typeInteraction: 'survey',
        config_category: configCategory as string
      })
    }
  }

  /**
   * @INFO Verificar si hay una encuesta de caracterización y cargarla en el store
   */
  public checkCharacterizationSurveyAvailable = async () => {
    const response = await this._checkCharacterizationSurveyAvailable()
    if(response?.code === 200 && response?.survey && response?.academic_resource_config){
      // @INFO Obtener el config category de la encuesta
      const configCategory = storeService?.getAcademicResourceConfigCategoryFromName('survey')
      this.addInteraction({
        academic_resource_config: response.academic_resource_config,
        survey: response.survey,
        typeInteraction: 'characterization_survey',
        config_category: configCategory as string
      })
    }
  }

  public checkPendingCertificationsWithPayment = async () => {
    const certificateService = new CertificateService()
    const response = await certificateService.checkPendingCertificationsWithPayment()
    if(response?.code === 200 && response?.certificate){
      this.addInteraction({
        pendingCertificate: response.certificate,
        typeInteraction: 'pending_certificate'
      })
    }
  }

  /**
   * @INFO Consultar una encuesta disponible para mostrar
   * @param _params
   */
  private _checkSurveyAvailable = async (_params: ICheckSurveyAvailableAPIParams) => {
    this.refreshAuthToken()

    let responseData: any

    const queryData: HttpCustomStructure = {
      method: 'GET',
      url: `/api/event/survey/check-survey-available/${_params.moodle_id}`,
      auth_token: this.token,
      params: _params
    }

    responseData = await this.Http.send(queryData)

    if (responseData.code === 200) return responseData

    return null
  }

  /**
   * @INFO Consultar una encuesta disponible para mostrar
   * @param _params
   */
  private _checkCharacterizationSurveyAvailable = async () => {
    this.refreshAuthToken()

    let responseData: any

    const queryData: HttpCustomStructure = {
      method: 'GET',
      url: `/api/event/survey/check-characterization-survey-available`,
      auth_token: this.token
    }

    responseData = await this.Http.send(queryData)

    if (responseData.code === 200) return responseData

    return null
  }

}

export default InteractionService
